/* eslint-disable */
import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Material UI table
import { MaterialReactTable } from "material-react-table";
import {
  Box,
  MenuItem,
  IconButton,
  TextField,
  InputAdornment,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Check";
import CancelIcon from "@mui/icons-material/Close";

import { getData, postData, putData } from "utils/Gateway";
import * as url from "helper/UrlHelper";

import TabsHeader from "components/Common/TabsHeader/TabsHeader";
import { commonHeaderLinkLists } from "helper/PatientManagementHelper/PatientManagementHelper";
import AccessDeniedView from "components/Common/AccessDeniedView/AccessDeniedView";
import AlertNotification from "components/Common/AlertNotification/AlertNotification";
import PrescriptionsHeader from "./Header/PrescriptionsHeader";
import {
  getDateFormatInSpanish,
  mergeListById,
} from "helper/Common/CommonHelper";
import LoadingSkeleton from "components/Common/ListLoadingSkeleton/LoadingSkeleton";
import SavePrescriptionPopup from "./Popup/SavePrescriptionPopup";
import { assetImages } from "constants";
import DeleteWarningModal from "components/Common/Modal/DeleteWarningModal";
import AssignUserModal from "components/Common/Modal/AssignUserModal";

const PrescriptionsListBody = () => {
  const { t } = useTranslation();

  const token = localStorage.getItem("token");
  const moduleAccess = localStorage.getItem("moduleaccess");
  const userInfo = JSON.parse(localStorage.getItem("userInfo"));

  const [isLoading, setIsLoading] = useState(false);
  const [prescriptionList, setPrescriptionList] = useState([]);
  const [rowSelection, setRowSelection] = useState({});
  const [selectedPrescriptionIds, setSelectedPrescriptionIds] = useState([]);
  const [selectedPrescriptionId, setSelectedPrescriptionId] = useState(null);

  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  // Inline editing state
  const [editingCell, setEditingCell] = useState({ rowId: null, column: null });
  const [editingValue, setEditingValue] = useState("");

  //function for get all prescriptions
  const getAllPrescriptions = async () => {
    try {
      setIsLoading(true);

      let requestUrl =
        url.API_BASE_URL + url.API_GET_ALL_MEDICAL_DOCUMENT;

      console.log("requestUrl", requestUrl);

      const response = await getData(requestUrl);

      setIsLoading(false);

      console.log("response", response);

      if (response.status) {
        setPrescriptionList(response.data);
      }
    } catch (error) {
      console.log(error.message);
    }
  };

  //function for update bulk prescription
  const updateBulkPrescriptionHandler = async () => {
    if (selectedPrescriptionIds.length > 0) {
      try {
        const prescriptionListToBeUpdate = prescriptionList
          .filter((item) => selectedPrescriptionIds.includes(item._id))
          .map((item) => {
            return {
              _id: item._id,
              name: item.name,
              provider: item.provider,
              testdate: item.testdate,
              resultdate: item.resultdate,
              referredby: item.referredby,
            };
          });

        let apiData = {
          medicaldocumentlist: prescriptionListToBeUpdate,
        };

        // console.log("apiData in update bulk prescription handler", apiData);

        let requestURL =
          url.API_BASE_URL +
          url.API_UPDATE_BULK_MEDICAL_DOCUMENT +
          ``;

        const response = await putData(requestURL, apiData);

        // console.log("response in update bulk prescription handler", response);

        if (response.status) {
          setMessageType("success");

          setTimeout(() => {
            setSelectedPrescriptionIds([]);
            setRowSelection({});
          }, 1500);
        } else {
          setMessageType("error");
        }

        setAlertMessage(response.message);
      } catch (error) {
        setAlertMessage(error.message);
        setMessageType("error");
      }
    } else {
      setAlertMessage("Please select at least one prescription");
      setMessageType("error");
    }
    setShowAlert(true);
  };

  //function for delete bulk prescription
  const deletePrescriptionHandler = async () => {
    if (selectedPrescriptionIds.length > 0) {
      try {
        const prescriptionListToBeDelete = prescriptionList.filter((item) =>
          selectedPrescriptionIds.includes(item._id)
        );

        let apiData = {
          medicaldocumentlist: prescriptionListToBeDelete,
        };

        let requestURL =
          url.API_BASE_URL +
          url.API_DELETE_BULK_MEDICAL_DOCUMENT +
          ``;

        const response = await putData(requestURL, apiData);

        console.log(response);

        if (response.status) {
          setMessageType("success");
          setTimeout(() => {
            setPrescriptionList((prevList) =>
              prevList.filter(
                (item) => !selectedPrescriptionIds.includes(item._id)
              )
            );

            setSelectedPrescriptionIds([]);
            setRowSelection({});
          }, 1500);
        } else {
          setMessageType("error");
        }

        setAlertMessage(response.message);
      } catch (error) {
        setAlertMessage(error.message);
        setMessageType("error");
      }
    } else {
      setAlertMessage("Please select at least one prescription");
      setMessageType("error");
    }
    setShowAlert(true);
  };

  //function for after any action
  const afterActionClose = (data = []) => {
    setPrescriptionList((prevList) => mergeListById(prevList, data));
  };

  useEffect(() => {
    getAllPrescriptions();
  }, []);

  // Save edited value
  const handleSaveEdit = (rowId, column) => {
    setPrescriptionList((prevList) =>
      prevList.map((item) =>
        item._id === rowId ? { ...item, [column]: editingValue } : item
      )
    );
    setEditingCell({ rowId: null, column: null });
    setEditingValue("");
    // Select the corresponding row after save
    setRowSelection((prevSelection) => ({
      ...prevSelection,
      [rowId]: true,
    }));
    // Also update selectedPrescriptionIds state
    setSelectedPrescriptionIds((prevIds) =>
      prevIds.includes(rowId) ? prevIds : [...prevIds, rowId]
    );
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingCell({ rowId: null, column: null });
    setEditingValue("");
  };

  const columns = useMemo(
    () => [
      {
        accessorKey: "#",
        header: t("Actions"),
        Cell: ({ row }) => (
          <button
            className="action_btn_mui"
            data-bs-toggle="offcanvas"
            data-bs-target="#savePrescriptionOffCanvas"
            aria-controls="savePrescriptionOffCanvas"
            onClick={() => {
              setSelectedPrescriptionId(row.original._id);
            }}
          >
            <span className="d-block material-symbols-outlined horz_icon">
              more_horiz
            </span>
          </button>
        ),
        enableColumnActions: false, // Hides the column action icon
        enableColumnDragging: false, // Hides the move icon
        enableSorting: false,
      },
      {
        header: "Name",
        accessorKey: "name",
        size: 200,
        Cell: ({ row }) =>
          editingCell.rowId === row.original._id &&
          editingCell.column === "name" ? (
            <TextField
              size="small"
              value={editingValue}
              onChange={(e) => setEditingValue(e.target.value)}
              autoFocus
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  border: "1.5px solid #b2b2b2ff", // updated to requested color
                  boxShadow: "none",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => handleSaveEdit(row.original._id, "name")}
                      sx={{
                        border: 1,
                        borderColor: "grey.main", // gray main
                        color: "grey.main", // gray main
                        bgcolor: "transparent",
                        mr: 0.5,
                        borderRadius: "8px",
                        minWidth: 28,
                        height: 28,
                        "&:hover": {
                          bgcolor: "grey.100", // light gray background
                          borderColor: "grey.dark", // gray dark
                          color: "grey.dark", // gray dark
                        },
                      }}
                    >
                      <SaveIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={handleCancelEdit}
                      sx={{
                        border: 1,
                        borderColor: "error.light",
                        color: "error.light",
                        bgcolor: "transparent",
                        borderRadius: "8px", // rounded rectangle
                        minWidth: 28, // reduced size
                        height: 28, // reduced size
                        "&:hover": {
                          bgcolor: "error.lighter",
                          borderColor: "error.dark",
                          color: "error.dark",
                        },
                      }}
                    >
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleSaveEdit(row.original._id, "name");
                if (e.key === "Escape") handleCancelEdit();
              }}
            />
          ) : (
            <span
              onClick={() => {
                setEditingCell({ rowId: row.original._id, column: "name" });
                setEditingValue(row.original.name || "");
              }}
              style={{ cursor: "pointer" }}
            >
              {row.original.name}
            </span>
          ),
      },
      {
        header: "Referred By",
        accessorKey: "referredby",
        size: 200,
        Cell: ({ row }) =>
          editingCell.rowId === row.original._id &&
          editingCell.column === "referredby" ? (
            <TextField
              size="small"
              value={editingValue}
              onChange={(e) => setEditingValue(e.target.value)}
              autoFocus
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  border: "1.5px solid #ced4da",
                  boxShadow: "none",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleSaveEdit(row.original._id, "referredby")
                      }
                      sx={{
                        border: 1,
                        borderColor: "grey.main",
                        color: "grey.main",
                        bgcolor: "transparent",
                        mr: 0.5,
                        borderRadius: "8px",
                        minWidth: 28,
                        height: 28,
                        "&:hover": {
                          bgcolor: "grey.100",
                          borderColor: "grey.dark",
                          color: "grey.dark",
                        },
                      }}
                    >
                      <SaveIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={handleCancelEdit}
                      sx={{
                        border: 1,
                        borderColor: "error.light",
                        color: "error.light",
                        bgcolor: "transparent",
                        borderRadius: "8px", // rounded rectangle
                        minWidth: 28,
                        height: 28,
                        "&:hover": {
                          bgcolor: "error.lighter",
                          borderColor: "error.dark",
                          color: "error.dark",
                        },
                      }}
                    >
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter")
                  handleSaveEdit(row.original._id, "referredby");
                if (e.key === "Escape") handleCancelEdit();
              }}
            />
          ) : (
            <span
              onClick={() => {
                setEditingCell({
                  rowId: row.original._id,
                  column: "referredby",
                });
                setEditingValue(row.original.referredby || "");
              }}
              style={{ cursor: "pointer" }}
            >
              {row.original.referredby}
            </span>
          ),
      },
      {
        header: "Provider",
        accessorKey: "provider",
        size: 200,
        Cell: ({ row }) =>
          editingCell.rowId === row.original._id &&
          editingCell.column === "provider" ? (
            <TextField
              size="small"
              value={editingValue}
              onChange={(e) => setEditingValue(e.target.value)}
              autoFocus
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  border: "1.5px solid #ced4da",
                  boxShadow: "none",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleSaveEdit(row.original._id, "provider")
                      }
                      sx={{
                        border: 1,
                        borderColor: "grey.main",
                        color: "grey.main",
                        bgcolor: "transparent",
                        mr: 0.5,
                        borderRadius: "8px",
                        minWidth: 28,
                        height: 28,
                        "&:hover": {
                          bgcolor: "grey.100",
                          borderColor: "grey.dark",
                          color: "grey.dark",
                        },
                      }}
                    >
                      <SaveIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={handleCancelEdit}
                      sx={{
                        border: 1,
                        borderColor: "error.light",
                        color: "error.light",
                        bgcolor: "transparent",
                        borderRadius: "8px", // rounded rectangle
                        minWidth: 28,
                        height: 28,
                        "&:hover": {
                          bgcolor: "error.lighter",
                          borderColor: "error.dark",
                          color: "error.dark",
                        },
                      }}
                    >
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter")
                  handleSaveEdit(row.original._id, "provider");
                if (e.key === "Escape") handleCancelEdit();
              }}
            />
          ) : (
            <span
              onClick={() => {
                setEditingCell({ rowId: row.original._id, column: "provider" });
                setEditingValue(row.original.provider || "");
              }}
              style={{ cursor: "pointer" }}
            >
              {row.original.provider}
            </span>
          ),
      },
      {
        header: "Test Date",
        accessorKey: "testdate",
        Cell: ({ row }) =>
          editingCell.rowId === row.original._id &&
          editingCell.column === "testdate" ? (
            <TextField
              size="small"
              type="date"
              value={
                editingValue ||
                (row.original.testdate
                  ? new Date(row.original.testdate).toISOString().slice(0, 10)
                  : "")
              }
              onChange={(e) => setEditingValue(e.target.value)}
              autoFocus
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  border: "1.5px solid #ced4da",
                  boxShadow: "none",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleSaveEdit(row.original._id, "testdate")
                      }
                      sx={{
                        border: 1,
                        borderColor: "grey.main",
                        color: "grey.main",
                        bgcolor: "transparent",
                        mr: 0.5,
                        borderRadius: "8px",
                        minWidth: 28,
                        height: 28,
                        "&:hover": {
                          bgcolor: "grey.100",
                          borderColor: "grey.dark",
                          color: "grey.dark",
                        },
                      }}
                    >
                      <SaveIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={handleCancelEdit}
                      sx={{
                        border: 1,
                        borderColor: "error.light",
                        color: "error.light",
                        bgcolor: "transparent",
                        borderRadius: "8px", // rounded rectangle
                        minWidth: 28,
                        height: 28,
                        "&:hover": {
                          bgcolor: "error.lighter",
                          borderColor: "error.dark",
                          color: "error.dark",
                        },
                      }}
                    >
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter")
                  handleSaveEdit(row.original._id, "testdate");
                if (e.key === "Escape") handleCancelEdit();
              }}
            />
          ) : (
            <div
              className="joined border-bottom-0"
              onClick={() => {
                setEditingCell({ rowId: row.original._id, column: "testdate" });
                setEditingValue(
                  row.original.testdate
                    ? new Date(row.original.testdate).toISOString().slice(0, 10)
                    : ""
                );
              }}
              style={{ cursor: "pointer" }}
            >
              <p className="d-flex align-items-center gap-2">
                <span className="d-block material-symbols-outlined icon-md">
                  calendar_month
                </span>
                <span className="d-block">
                  {getDateFormatInSpanish(row.original.testdate)}
                </span>
              </p>
            </div>
          ),
      },
      {
        header: "Result Date",
        accessorKey: "resultdate",
        Cell: ({ row }) =>
          editingCell.rowId === row.original._id &&
          editingCell.column === "resultdate" ? (
            <TextField
              size="small"
              type="date"
              value={
                editingValue ||
                (row.original.resultdate
                  ? new Date(row.original.resultdate).toISOString().slice(0, 10)
                  : "")
              }
              onChange={(e) => setEditingValue(e.target.value)}
              autoFocus
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  border: "1.5px solid #ced4da",
                  boxShadow: "none",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() =>
                        handleSaveEdit(row.original._id, "resultdate")
                      }
                      sx={{
                        border: 1,
                        borderColor: "grey.main",
                        color: "grey.main",
                        bgcolor: "transparent",
                        mr: 0.5,
                        borderRadius: "8px",
                        minWidth: 28,
                        height: 28,
                        "&:hover": {
                          bgcolor: "grey.100",
                          borderColor: "grey.dark",
                          color: "grey.dark",
                        },
                      }}
                    >
                      <SaveIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={handleCancelEdit}
                      sx={{
                        border: 1,
                        borderColor: "error.light",
                        color: "error.light",
                        bgcolor: "transparent",
                        borderRadius: "8px", // rounded rectangle
                        minWidth: 28,
                        height: 28,
                        "&:hover": {
                          bgcolor: "error.lighter",
                          borderColor: "error.dark",
                          color: "error.dark",
                        },
                      }}
                    >
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter")
                  handleSaveEdit(row.original._id, "resultdate");
                if (e.key === "Escape") handleCancelEdit();
              }}
            />
          ) : (
            <div
              className="joined border-bottom-0"
              onClick={() => {
                setEditingCell({
                  rowId: row.original._id,
                  column: "resultdate",
                });
                setEditingValue(
                  row.original.resultdate
                    ? new Date(row.original.resultdate)
                        .toISOString()
                        .slice(0, 10)
                    : ""
                );
              }}
              style={{ cursor: "pointer" }}
            >
              <p className="d-flex align-items-center gap-2">
                <span className="d-block material-symbols-outlined icon-md">
                  calendar_month
                </span>
                <span className="d-block">
                  {getDateFormatInSpanish(row.original.resultdate)}
                </span>
              </p>
            </div>
          ),
      },
      {
        header: "Assigned To",
        accessorKey: "assignedpersonname",
        Cell: ({ row }) => (
          <div
            className={
              row.original.assignedpersonname === ""
                ? "d-none"
                : "owner border-bottom-0 d-flex align-items-center gap-1"
            }
          >
            <img
              className="rounded-circle"
              height={35}
              width={35}
              src={
                row.original.assignedpersonimage == ""
                  ? assetImages.defaultUser
                  : url.SERVER_URL + row.original.assignedpersonimage
              }
              alt="Babcock"
            />
            <p>
              <span className="d-block">{row.original.assignedpersonname}</span>
            </p>
            {/* <button
              onClick={() => {
                createTaskChatHandler(row.original._id);
              }}
              className={
                row.original.moderator.toString() === userInfo._id.toString()
                  ? "d-none"
                  : "d-flex align-items-center ms-auto border-0 bg-light"
              }
            >
              <span className="d-block material-symbols-outlined icon-md text-primary">
                chat
              </span>
            </button> */}
          </div>
        ),
      },
    ],
    [editingCell, editingValue]
  );

  //initialize the column order
  const columnOrder = [
    "#",
    "mrt-row-select",
    ...columns.map((c) => c.accessorKey),
  ]; //array of column ids (Initializing is optional as of v2.10.0)

  useEffect(() => {
    const selectedIdsArray = Object.keys(rowSelection).filter(
      (key) => rowSelection[key]
    );

    setSelectedPrescriptionIds(selectedIdsArray);
  }, [rowSelection]);

  const onAlertClose = () => {
    setShowAlert(false);
    setAlertMessage("");
    setMessageType("");
  };

  if (
    userInfo.role.slug === "ADMIN" ||
    userInfo.role.slug === "SUPER_ADMIN" ||
    moduleAccess.includes("MOD_APPOINTMENT_MANAGEMENT")
  ) {
    return (
      <div id="content_wrapper">
        <section className="crm-wrapper bg-white pb-5">
          <TabsHeader
            commonHeaderObject={commonHeaderLinkLists(t)}
            activeOption={t("Prescriptions")}
          />
          <div className="container-fluid px-lg-5">
            <PrescriptionsHeader
              reloadList={getAllPrescriptions}
              selectedItemIds={selectedPrescriptionIds}
              updateBulkHandler={updateBulkPrescriptionHandler}
            />

            {isLoading ? (
              <LoadingSkeleton />
            ) : (
              <div className="table-wrapper">
                <MaterialReactTable
                  columns={columns}
                  data={prescriptionList}
                  enableGrouping // to enable grouping of column
                  enableRowSelection // enable showing checkbox in each row
                  getRowId={(row) => row._id}
                  onRowSelectionChange={setRowSelection}
                  state={{ rowSelection, columnOrder }}
                  initialState={{ rowSelection, columnOrder }} // to set the initial state of column order
                  defaultColumn={{
                    minSize: 20,
                    maxSize: 300,
                    size: 50, //make columns wider by default
                  }}
                  muiTableContainerProps={{
                    sx: {
                      maxHeight: "60vh",
                    },
                  }}
                  enableStickyHeader
                />
              </div>
            )}
          </div>

          {/* popup section */}
          <SavePrescriptionPopup
            selectedPrescriptionId={selectedPrescriptionId}
            setSelectedPrescriptionId={setSelectedPrescriptionId}
            afterActionClose={afterActionClose}
            setShowAlert={setShowAlert}
            setAlertMessage={setAlertMessage}
            setMessageType={setMessageType}
          />

          <AssignUserModal
            moduleName={"prescription"}
            selectedIds={selectedPrescriptionIds}
            onModalClose={() => {
              setRowSelection({});
              setSelectedPrescriptionIds([]);
            }}
            afterModalClose={afterActionClose}
            setShowAlert={setShowAlert}
            setAlertMessage={setAlertMessage}
            setMessageType={setMessageType}
          />

          <DeleteWarningModal
            moduleName={"prescription"}
            onDelete={deletePrescriptionHandler}
            onCancelDelete={() => {
              setRowSelection({});
              setSelectedPrescriptionIds([]);
            }}
          />

          {showAlert && (
            <AlertNotification
              showAlert={showAlert}
              message={alertMessage}
              alertType={messageType}
              onClose={onAlertClose}
            />
          )}
        </section>
      </div>
    );
  } else {
    return <AccessDeniedView />;
  }
};

export default PrescriptionsListBody;
